import { ColumnDef, ColumnMeta } from '@tanstack/react-table'
import { SubscriptionFeatureSettings } from 'models'
import { IdEventV4 } from 'models/event'

import { activeCallColumn } from './activeCallColumn'
import { botColumn } from './botColumn'
import { browserDetailsColumn } from './browserDetailsColumn'
import { browserTamperingColumn } from './browserTamperingColumn'
import { clonedAppColumn } from './clonedAppColumn'
import { dateColumn } from './dateColumn'
import { developerToolsColumn } from './developerToolsColumn'
import { deviceRarityColumn } from './deviceRarityColumn'
import { emulatorColumn } from './emulatorColumn'
import { environmentColumn } from './environmentColumn'
import { fraudScoreColumn } from './fraudScoreColumn'
import { fridaColumn } from './fridaColumn'
import { highRecallIdColumn } from './highRecallIdColumn'
import { incognitoColumn } from './incognitoColumn'
import { ipAddressColumn } from './ipAddressColumn'
import { ipBlocklistColumn } from './ipBlocklistColumn'
import { jailbrokenColumn } from './jailbrokenColumn'
import { linkedIdColumn } from './linkedIdColumn'
import { locationSpoofingColumn } from './locationSpoofingColumn'
import { mitmAttackColumn } from './mitmAttackColumn'
import { originColumn } from './originColumn'
import { osDetailsColumn } from './osDetailsColumn'
import { privacySettingsColumn } from './privacySettingsColumn'
import { proximityIdColumn } from './proximityIdColumn'
import { replayedColumn } from './replayedColumn'
import { requestIdColumn } from './requestIdColumn'
import { residentialProxyColumn } from './residentialProxyColumn'
import { rootedDeviceColumn } from './rootedDeviceColumn'
import { sdkColumn } from './sdkColumn'
import { simulatorColumn } from './simulatorColumn'
import { suspectScoreColumn } from './suspectScoreColumn'
import { tagColumn } from './tagColumn'
import { torColumn } from './torColumn'
import { urlColumn } from './urlColumn'
import { virtualMachineColumn } from './virtualMachineColumn'
import { visitorIdColumn } from './visitorIdColumn'
import { vpnColumn } from './vpnColumn'

export function getColumnDefinitions({
  workspaceId,
  isFraudScoreEnabled = false,
}: {
  workspaceId: string
  /** Adds the Fraud Score column (hidden by default) when Fraud Score is enabled for the workspace. */
  isFraudScoreEnabled?: boolean
}) {
  return [
    requestIdColumn,
    dateColumn(),
    visitorIdColumn({ workspaceId }),
    highRecallIdColumn({ requiredFeature: 'highRecallEnabled' }),
    proximityIdColumn(),
    linkedIdColumn,
    environmentColumn(),
    ipAddressColumn(),
    browserDetailsColumn,
    osDetailsColumn,
    sdkColumn(),
    originColumn(),
    urlColumn(),
    replayedColumn,
    tagColumn(),
    suspectScoreColumn(),
    ...(isFraudScoreEnabled ? [fraudScoreColumn({ workspaceId })] : []),
    ipBlocklistColumn(),
    mitmAttackColumn(),
    deviceRarityColumn(),
    residentialProxyColumn(),
    botColumn(),
    incognitoColumn(),
    vpnColumn(),
    torColumn(),
    browserTamperingColumn(),
    virtualMachineColumn(),
    privacySettingsColumn(),
    developerToolsColumn(),
    jailbrokenColumn(),
    emulatorColumn(),
    simulatorColumn(),
    rootedDeviceColumn(),
    clonedAppColumn(),
    fridaColumn(),
    locationSpoofingColumn(),
    activeCallColumn(),
  ]
}

/**
 * All valid column IDs for the events table. `fraudScore` is only defined when Fraud Score is enabled.
 * This is the single source of truth — DEFAULT_COLUMN_VISIBILITY is typed against this.
 */
export const COLUMN_IDS = [
  'requestId',
  'timestamp',
  'visitorId',
  'highRecallId',
  'proximityId',
  'linkedId',
  'environment',
  'ip',
  'browserDetails',
  'osDetails',
  'sdk',
  'origin',
  'url',
  'replayed',
  'tag',
  'suspectScore',
  'fraudScore',
  'ipBlocklist',
  'mitmAttack',
  'deviceRarity',
  'residentialProxy',
  'bot',
  'incognito',
  'vpn',
  'tor',
  'browserTampering',
  'virtualMachine',
  'privacySettings',
  'developerTools',
  'jailbroken',
  'emulator',
  'simulator',
  'rootedDevice',
  'clonedApp',
  'frida',
  'locationSpoofing',
  'activeCall',
] as const

export type ColumnId = (typeof COLUMN_IDS)[number]

/**
 * Default visibility for each column. TypeScript enforces that every ColumnId has an entry.
 */
export const DEFAULT_COLUMN_VISIBILITY: Record<ColumnId, boolean> = {
  requestId: true,
  timestamp: true,
  visitorId: true,
  highRecallId: false,
  linkedId: false,
  proximityId: false,
  environment: false,
  ip: true,
  browserDetails: false,
  osDetails: false,
  sdk: true,
  origin: true,
  url: false,
  replayed: false,
  tag: false,
  suspectScore: false,
  fraudScore: false,
  ipBlocklist: false,
  residentialProxy: false,
  mitmAttack: false,
  deviceRarity: false,
  bot: false,
  incognito: false,
  vpn: false,
  tor: false,
  browserTampering: false,
  virtualMachine: false,
  privacySettings: false,
  developerTools: false,
  jailbroken: false,
  emulator: false,
  simulator: false,
  rootedDevice: false,
  clonedApp: false,
  frida: false,
  locationSpoofing: false,
  activeCall: false,
}

/** Metadata for columns that require a specific subscription feature to be enabled. */
export interface FeatureGatedColumnMeta extends ColumnMeta<IdEventV4, unknown> {
  requiredFeature?: keyof SubscriptionFeatureSettings
}

/** Type-safe accessor for feature-gated column meta. */
export function getRequiredFeature(col: ColumnDef<IdEventV4, unknown>): keyof SubscriptionFeatureSettings | undefined {
  return (col.meta as FeatureGatedColumnMeta | undefined)?.requiredFeature
}
